// Pure chart math (unit-tested): nice ticks, linear scales, sparse x labels.

/** "Nice" step for a range: 1, 2, 2.5 or 5 × 10^n. */
export function niceStep(span: number, targetTicks: number): number {
  if (!(span > 0) || targetTicks < 1) return 1;
  const raw = span / targetTicks;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const f = raw / pow;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return nice * pow;
}

/**
 * Clean ticks covering [min, max]. `zero` forces the domain to include 0 (bars).
 * Integers only when `integer` (counts): steps are at least 1.
 */
export function niceTicks(min: number, max: number, opts: { target?: number; zero?: boolean; integer?: boolean } = {}): number[] {
  const target = opts.target ?? 4;
  let lo = opts.zero ? Math.min(0, min) : min;
  let hi = opts.zero ? Math.max(0, max) : max;
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [0, 1];
  if (lo === hi) {
    const pad = Math.max(1, Math.abs(hi) * 0.05);
    hi += pad;
    if (!opts.zero) lo -= pad;
  }
  let step = niceStep(hi - lo, target);
  if (opts.integer) step = Math.max(1, Math.round(step));
  const start = Math.floor(lo / step) * step;
  const end = Math.ceil(hi / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= end + step / 2; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
  return ticks;
}

export function linear(domain: [number, number], range: [number, number]): (v: number) => number {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const k = d1 === d0 ? 0 : (r1 - r0) / (d1 - d0);
  return (v) => r0 + (v - d0) * k;
}

/** Indices to label on an x axis of `n` points so labels are ≥ `minGapPx` apart (first + last always). */
export function sparseLabelIndices(n: number, widthPx: number, minGapPx = 64): number[] {
  if (n <= 0) return [];
  if (n === 1) return [0];
  const per = widthPx / (n - 1);
  const every = Math.max(1, Math.ceil(minGapPx / Math.max(per, 1)));
  const out: number[] = [];
  for (let i = 0; i < n; i += every) out.push(i);
  const last = n - 1;
  if (out[out.length - 1] !== last) {
    // Drop the previous label if it would collide with the last one.
    if (out.length > 1 && (last - out[out.length - 1]!) * per < minGapPx) out.pop();
    out.push(last);
  }
  return out;
}

/** Compact axis label: 1200 → "1.2K". */
export function axisLabel(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${+(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 10_000) return `${+(v / 1000).toFixed(0)}K`;
  if (Math.abs(v) >= 1000) return `${+(v / 1000).toFixed(1)}K`;
  return String(+v.toFixed(2));
}

/** Rounded-top bar path (data-end rounded, baseline square). */
export function roundedTopBar(x: number, y: number, w: number, h: number, r: number): string {
  if (h <= 0 || w <= 0) return "";
  const rr = Math.min(r, w / 2, h);
  return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`;
}

/** Rounded-right horizontal bar (data-end on the right, baseline on the left). */
export function roundedRightBar(x: number, y: number, w: number, h: number, r: number): string {
  if (h <= 0 || w <= 0) return "";
  const rr = Math.min(r, h / 2, w);
  return `M${x},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h - rr}Q${x + w},${y + h} ${x + w - rr},${y + h}H${x}Z`;
}
